import { Box, HStack, VStack } from 'native-base';
import React, { useMemo, useState } from 'react';
import { useI18n, useSpringBigStore } from '@springbig/shared';
import { faGifts, faLock } from '@fortawesome/free-solid-svg-icons';
import { useAppNavigate } from '~/pages';
import ConditionalForm, { AutoConnectFieldType } from './MemberForm';
import { useGeneralStyles, useOffersStyles } from '~/shared/styles';
import { Caption, H3 } from '~/shared/Typography';
import { useToast } from '~/hooks/toast';
import Button from '~/shared/Button';
import AppDownloadModal, { DesiredActionType } from './AppDownloadModal';
import { SpIcon } from '~/shared/Icon';

type DesiredActionProps = {
    desiredAction: DesiredActionType;
    onGetOffer: (desiredAction: DesiredActionType) => void;
};

type DesiredActionsProps = { desiredActions: DesiredActionType[], merchantName: string };

const DesiredActions = (props: DesiredActionsProps) => {

    const { navigate } = useAppNavigate();
    const [showModal, setShowModal] = useState<boolean>(false);
    const [showAppDownloadModal, setShowAppDownloadModal] = useState<boolean>(false);
    const [selectedTypeId, setSelectedTypeId] = useState<AutoConnectFieldType | null>();
    const [selectedDesiredAction, setSelectedDesiredAction] = useState<DesiredActionType | null>();
    const { walletId } = useSpringBigStore();
    const { successToast } = useToast();
    const { t } = useI18n();

    const onGetOffer = (desiredAction: DesiredActionType) => {

        switch (desiredAction.type_id) {
            case 8:
            case 10:
                setSelectedTypeId(desiredAction.type_id);
                setShowModal(true);
                return;
            case 9:
                successToast(t('alerts.interest_auto_connect'));
                navigate(`w/${walletId}/profile/interests`, { state: { isInterestScreen: true } });
                return;
            case 26:
                setSelectedTypeId(26);
                setSelectedDesiredAction(desiredAction);
                setShowAppDownloadModal(true);
                return;
        }
    };

    return (
        <Box width='100%' mt={2}>
            {
                props.desiredActions.map((action) => {

                    if ([8, 9, 10, 26].includes(action.type_id)) {
                        return <DesiredAction
                            key={action.name}
                            desiredAction={action}
                            onGetOffer={onGetOffer}
                        />;
                    }

                    return <></>;
                })
            }
            {showModal && selectedTypeId &&
                <ConditionalForm
                    isOpen={showModal}
                    closeModal={() => setShowModal(false)}
                    fieldType={selectedTypeId}
                />
            }
            {showAppDownloadModal &&
                <AppDownloadModal
                    isOpen={showAppDownloadModal}
                    action={selectedDesiredAction}
                    merchantName={props.merchantName}
                    closeModal={() => setShowAppDownloadModal(false)}
                />
            }
        </Box>
    );
};

const DesiredAction = (props: DesiredActionProps) => {

    const { t } = useI18n();
    const gStyles = useGeneralStyles();
    const {
        text,
        icon
    } = useOffersStyles();

    const desiredAction = useMemo(() => {

        switch (props.desiredAction.type_id) {
            case 8:
                return t('general.birthday');
            case 9:
                return props.desiredAction.name;
            case 10:
                return t('general.email');
            case 26:
                return props.desiredAction.name;
            default:
                return t('general.birthday');
        }
    }, [props.desiredAction.type_id, t]);

    const bonusType = useMemo(() => {

        if (props.desiredAction.rewards && props.desiredAction.rewards.length > 0) {
            switch (props.desiredAction.rewards[0].bonus_type) {
                case 'multiply':
                    return '*';
                case 'addition':
                    return '+';
                default:
                    return '';
            }
        }
    }, []);

    return (
        <VStack
            bg={gStyles.section_background}
            space={2}
            py={4}
            mb={2}
            alignItems={'center'}
        >
            <HStack space={2}>
                <SpIcon
                    icon={faLock}
                    color={icon}
                />
                <Caption
                    color={text}
                    fontWeight="bold"
                    textAlign='center'
                    text='offers.unlock'
                />
            </HStack>
            <HStack
                alignItems="center"
                w={'100%'}
                justifyContent="space-evenly"
            >
                <Box
                    w={24}
                    h={24}
                    borderRadius={12}
                    bg={gStyles.screen_background}
                    alignItems='center'
                    justifyContent={'center'}
                >
                    <SpIcon
                        color={icon}
                        icon={faGifts}
                        size={80}
                    />
                </Box>
                <Box w={'45%'}>
                    <H3 textAlign={'center'} color={text}>
                        { desiredAction }
                    </H3>
                    <Caption textAlign={'center'}>
                        {
                            props.desiredAction.rewards
                            && props.desiredAction.rewards.length > 0
                            && `${bonusType} ${props.desiredAction.rewards[0].name}`
                        }
                    </Caption>
                </Box>
                <Button
                    bg={gStyles.button_background}
                    _text={{
                        color: gStyles.button_text,
                    }}
                    h={50}
                    w={'20%'}
                    onPress={() => props.onGetOffer(props.desiredAction)}
                >
                    { t(props.desiredAction.type_id === 26 ? 'general.download_app' : 'offers.add') }
                </Button>
            </HStack>
            {
                (props.desiredAction.type_id !== 26 || (props.desiredAction.rewards && props.desiredAction.rewards.length > 0)) &&
                    <Caption
                        color={text}
                        width='95%'
                        textAlign='center'
                        text={props.desiredAction.type_id === 26 ? 'offers.app_download_desirable_action' : 'offers.desirable_action'}
                        textValues={[desiredAction, (props.desiredAction.rewards && props.desiredAction.rewards.length > 0) ? `${props.desiredAction.rewards[0].name}` : '']}
                    />
            }
        </VStack>
    );
};

export default DesiredActions;
